import React, { FC, useMemo } from 'react'
import { Table } from '@wmgtech/legato'
import { ColumnsType } from '@wmgtech/legato/build/components/Table/types'

import { gpsStatuses } from 'modulesCommon/ProductTable/constants'
import { TableRowActions } from 'modulesCommon/ProductTable/components'
import { capitalizeFirstLetter } from 'shared/utils/capitalizeFirstLetter'
import { formatDate } from 'shared/utils/formatDate'

import { ActionButton } from './ActionButton'
import { StatusField, ProductTitleField, ProductTypeField, ReleaseDateField } from './Fields'

import { IProductForm, TFields } from './types'
import { useRow } from 'components/Project/Tables'
import { ProductTableProps } from './productTable.props'

import styles from './product-table.module.scss'

export const ProductTable: FC<ProductTableProps> = ({ children, data, fields }) => {
  const { condition, onRow } = useRow<IProductForm>('editRow')

  const fieldDictionary = useMemo<Record<TFields, ColumnsType<unknown>>>(
    () => ({
      name: {
        dataIndex: 'name',
        key: 'name',
        title: 'Product Title',
        sortable: false,
        width: '32%',
        align: 'left',
        className: 'table__cell',
        render: condition([(v) => v, (v, r, idx) => <ProductTitleField idx={idx} />])
      },
      configuration: {
        dataIndex: 'configuration',
        key: 'configuration',
        title: 'Type of Product',
        sortable: false,
        width: '15%',
        align: 'left',
        className: 'table__cell',
        render: condition([
          (configuration) => capitalizeFirstLetter(configuration),
          (v, r, idx) => <ProductTypeField idx={idx} />
        ])
      },
      gps_status: {
        dataIndex: 'gps_status',
        key: 'gps_status',
        title: 'Status',
        sortable: false,
        width: '22%',
        align: 'left',
        className: 'table__cell',
        render: condition([
          (gps_status) => gpsStatuses[gps_status] || 'No Track Status',
          (v, r, idx) => <StatusField idx={idx} />
        ])
      },
      publish_date: {
        dataIndex: 'publish_date',
        key: 'publish_date',
        title: 'Release Date',
        sortable: false,
        width: '20%',
        align: 'left',
        ellipsis: true,
        className: 'table__cell',
        render: condition([
          (publish_date) =>
            formatDate({
              date: publish_date,
              withTime: false
            }),
          (v, r, idx) => <ReleaseDateField idx={idx} />
        ])
      }
    }),
    [condition]
  )

  const columns = useMemo<ColumnsType<unknown>[]>(
    () =>
      fields
        .map((f) => fieldDictionary[f])
        .concat([
          {
            dataIndex: 'manager',
            key: 'manager',
            sortable: false,
            width: '110px',
            align: 'right',
            className: 'table__cell',
            render: condition([
              (_, record, idx) => <TableRowActions>{children(record, idx)}</TableRowActions>,
              () => <ActionButton />
            ])
          }
        ]),
    [children, condition, fieldDictionary, fields]
  )

  return (
    <div>
      <Table
        className={styles.table}
        columns={columns}
        tableLayout="fixed"
        data={data}
        rowKey={(d: any) => d.id}
        pagination={false}
        condensed={true}
        onRow={onRow}
      />
    </div>
  )
}
